import React from "react";
import { AlertTriangle, RefreshCw, WifiOff } from "lucide-react";

interface ErrorViewProps {
  /** エラーの種類。省略時は "generic" */
  variant?: "generic" | "network" | "not-found";
  title?: string;
  message?: string;
  onRetry?: () => void;
}

const VARIANTS: Record<
  NonNullable<ErrorViewProps["variant"]>,
  {
    Icon: React.ElementType;
    defaultTitle: string;
    defaultMessage: string;
    iconColor: string;
    glowColor: string;
  }
> = {
  generic: {
    Icon: AlertTriangle,
    defaultTitle: "エラーが発生しました",
    defaultMessage:
      "予期しないエラーが発生しました。しばらくしてからもう一度お試しください。",
    iconColor: "text-rose-400",
    glowColor: "bg-rose-500/20",
  },
  network: {
    Icon: WifiOff,
    defaultTitle: "接続できません",
    defaultMessage: "ネットワーク接続を確認してから、もう一度お試しください。",
    iconColor: "text-amber-400",
    glowColor: "bg-amber-500/20",
  },
  "not-found": {
    Icon: AlertTriangle,
    defaultTitle: "ページが見つかりません",
    defaultMessage: "お探しのページは存在しないか、移動した可能性があります。",
    iconColor: "text-blue-400",
    glowColor: "bg-blue-500/20",
  },
};

const ErrorView: React.FC<ErrorViewProps> = ({
  variant = "generic",
  title,
  message,
  onRetry,
}) => {
  const { Icon, defaultTitle, defaultMessage, iconColor, glowColor } =
    VARIANTS[variant];

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-slate-900 to-black items-center justify-center px-8 animate-in fade-in duration-300">
      {/* Glow circle */}
      <div
        className={`w-24 h-24 rounded-full ${glowColor} blur-2xl absolute`}
      />

      {/* Icon */}
      <div className="relative flex items-center justify-center w-20 h-20 rounded-xl bg-white/5 border border-white/10 mb-8 shadow-2xl">
        <Icon size={36} className={iconColor} />
      </div>

      {/* Text */}
      <h1 className="text-xl font-black text-white tracking-tight mb-3 text-center uppercase">
        {title ?? defaultTitle}
      </h1>
      <p className="text-slate-400 text-sm font-medium text-center leading-relaxed max-w-xs">
        {message ?? defaultMessage}
      </p>

      {/* Retry button */}
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-10 flex items-center gap-2 bg-white text-slate-900 font-bold px-8 py-3.5 rounded-lg shadow-lg active:scale-95 transition-all uppercase tracking-widest text-xs"
        >
          <RefreshCw size={14} />
          再試行
        </button>
      )}

      {/* Error code watermark */}
      <p className="absolute bottom-10 text-white/10 text-[10px] font-black tracking-widest uppercase">
        Darts Life
      </p>
    </div>
  );
};

export default ErrorView;
